import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiX, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import DateInput from '../components/DateInput';
import { formatDateForInput } from '../utils/dateUtils';

const TrainerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const [formData, setFormData] = useState({
    branch_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    specialization: '',
    certifications: [],
    experience_years: '',
    joining_date: new Date().toISOString().split('T')[0],
    salary: '',
    commission_percent: '0',
    availability_schedule: {
      monday: { available: true, start: '06:00', end: '22:00' },
      tuesday: { available: true, start: '06:00', end: '22:00' },
      wednesday: { available: true, start: '06:00', end: '22:00' },
      thursday: { available: true, start: '06:00', end: '22:00' },
      friday: { available: true, start: '06:00', end: '22:00' },
      saturday: { available: true, start: '06:00', end: '22:00' },
      sunday: { available: false, start: '06:00', end: '22:00' }
    }
  });

  const [newCertification, setNewCertification] = useState({ name: '', year: '' });

  useEffect(() => {
    fetchBranches();
    if (isEditMode) {
      fetchTrainerDetails();
    }
  }, [id]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches?is_active=true');
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchTrainerDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trainers/${id}`);
      const trainer = response.data.data;

      const formattedDob = formatDateForInput(trainer.date_of_birth);
      const formattedJoiningDate = formatDateForInput(trainer.joining_date);

      // Safely parse certifications
      let certifications = [];
      try {
        if (trainer.certifications) {
          const parsed = typeof trainer.certifications === 'string'
            ? JSON.parse(trainer.certifications)
            : trainer.certifications;
          certifications = Array.isArray(parsed) ? parsed : [];
        }
      } catch (error) {
        console.error('Failed to parse certifications:', error);
        certifications = [];
      }

      // Safely parse and normalize availability schedule
      let availability_schedule = formData.availability_schedule;
      try {
        if (trainer.availability_schedule) {
          const parsed = typeof trainer.availability_schedule === 'string'
            ? JSON.parse(trainer.availability_schedule)
            : trainer.availability_schedule;

          // Normalize the schedule to ensure consistent structure with day names
          const normalizedSchedule = {};
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

          days.forEach(day => {
            // Check if the parsed data has this day
            if (parsed[day]) {
              normalizedSchedule[day] = {
                available: parsed[day].available !== undefined ? parsed[day].available : true,
                start: parsed[day].start || '06:00',
                end: parsed[day].end || '22:00'
              };
            } else {
              // Default values for missing days
              normalizedSchedule[day] = {
                available: day !== 'sunday',
                start: '06:00',
                end: '22:00'
              };
            }
          });

          availability_schedule = normalizedSchedule;
        }
      } catch (error) {
        console.error('Failed to parse availability schedule:', error);
        availability_schedule = formData.availability_schedule;
      }

      setFormData({
        branch_id: trainer.branch_id || '',
        first_name: trainer.first_name || '',
        last_name: trainer.last_name || '',
        email: trainer.email || '',
        phone: trainer.phone || '',
        date_of_birth: formattedDob,
        gender: trainer.gender || '',
        address: trainer.address || '',
        city: trainer.city || '',
        state: trainer.state || '',
        pincode: trainer.pincode || '',
        specialization: trainer.specialization || '',
        certifications: certifications,
        experience_years: trainer.experience_years || '',
        joining_date: formattedJoiningDate || new Date().toISOString().split('T')[0],
        salary: trainer.salary || '',
        commission_percent: trainer.commission_percent || '0',
        availability_schedule: availability_schedule
      });

      if (trainer.photo) {
        setPhotoPreview(trainer.photo);
      }
    } catch (error) {
      console.error('Error fetching trainer:', error);
      toast.error('Failed to load trainer details');
      navigate('/trainers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addCertification = () => {
    if (newCertification.name && newCertification.year) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, { ...newCertification }]
      }));
      setNewCertification({ name: '', year: '' });
    }
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleScheduleChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability_schedule: {
        ...prev.availability_schedule,
        [day]: {
          ...prev.availability_schedule[day],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'certifications' || key === 'availability_schedule') {
          submitData.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      // Add photo if selected
      if (photoFile) {
        submitData.append('photo', photoFile);
      }

      if (isEditMode) {
        await api.put(`/trainers/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Trainer updated successfully');
      } else {
        await api.post('/trainers', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Trainer created successfully');
      }

      navigate('/trainers');
    } catch (error) {
      console.error('Error saving trainer:', error);
      toast.error(error.response?.data?.message || 'Failed to save trainer');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditMode ? 'Edit Trainer' : 'Add New Trainer'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? 'Update trainer information' : 'Create a new trainer profile'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Profile Photo</h2>
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl text-gray-400">
                  {formData.first_name?.charAt(0)}{formData.last_name?.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                <FiUpload /> Upload Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-gray-500 mt-2">Recommended: Square image, at least 400x400px</p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <DateInput
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={(value) => setFormData(prev => ({ ...prev, date_of_birth: value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="DD/MM/YYYY"
              />
              <p className="mt-1 text-xs text-gray-500">Enter date as: DD/MM/YYYY (e.g., 15/05/2000)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch <span className="text-red-500">*</span>
              </label>
              <select
                name="branch_id"
                value={formData.branch_id}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Branch</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Professional Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Professional Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specialization
              </label>
              <input
                type="text"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                placeholder="e.g., Yoga, Cardio, Strength Training"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience (Years)
              </label>
              <input
                type="number"
                name="experience_years"
                value={formData.experience_years}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Joining Date <span className="text-red-500">*</span>
              </label>
              <DateInput
                name="joining_date"
                value={formData.joining_date}
                onChange={(value) => setFormData(prev => ({ ...prev, joining_date: value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="DD/MM/YYYY"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Enter date as: DD/MM/YYYY (e.g., 15/05/2024)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Salary (₹)
              </label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commission (%)
              </label>
              <input
                type="number"
                name="commission_percent"
                value={formData.commission_percent}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Certifications */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Certifications
              </label>
              <div className="space-y-2 mb-3">
                {formData.certifications.map((cert, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{cert.name}</p>
                      <p className="text-sm text-gray-500">Year: {cert.year}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCertification(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCertification.name}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Certification Name"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  value={newCertification.year}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, year: e.target.value }))}
                  placeholder="Year"
                  className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addCertification}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <FiPlus />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Availability Schedule */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Availability Schedule</h2>
          <div className="space-y-3">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
              formData.availability_schedule[day] && (
                <div key={day} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-32">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.availability_schedule[day].available}
                        onChange={(e) => handleScheduleChange(day, 'available', e.target.checked)}
                        className="rounded"
                      />
                      <span className="font-medium capitalize">{day}</span>
                    </label>
                  </div>
                  {formData.availability_schedule[day].available && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={formData.availability_schedule[day].start}
                        onChange={(e) => handleScheduleChange(day, 'start', e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded"
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={formData.availability_schedule[day].end}
                        onChange={(e) => handleScheduleChange(day, 'end', e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded"
                      />
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate('/trainers')}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiX /> Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400"
          >
            <FiSave /> {loading ? 'Saving...' : isEditMode ? 'Update Trainer' : 'Create Trainer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TrainerForm;
